<app-page-breadcrumb pageTitle="Plantillas de chatflow" />

<div class="space-y-6">
  <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
    <div class="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
      <div class="max-w-3xl">
        <h2 class="text-xl font-semibold text-gray-800 dark:text-white">Biblioteca de plantillas</h2>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Plantillas del sistema y plantillas propias de tu negocio. Las propias se crean con "Crear plantilla"
          o desde la lista de chatflows con "Guardar como plantilla", y son las únicas que puedes eliminar.
        </p>
      </div>

      <div class="flex flex-wrap gap-2">
        <button type="button" (click)="crearPlantilla()"
          class="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600">
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
          </svg>
          Crear plantilla
        </button>
        <a routerLink="/chatflows"
          class="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
          </svg>
          Volver a chatflows
        </a>
      </div>
    </div>

    <div class="mt-6 grid gap-4 md:grid-cols-3">
      <div class="rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-950/40">
        <div class="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">Del sistema</div>
        <div class="mt-2 text-2xl font-semibold text-gray-800 dark:text-white">{{ totalSistema }}</div>
      </div>
      <div class="rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-950/40">
        <div class="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">Propias</div>
        <div class="mt-2 text-2xl font-semibold text-gray-800 dark:text-white">{{ totalPropias }}</div>
      </div>
      <div>
        <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Buscar</label>
        <div class="relative">
          <span class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M21 21l-4.35-4.35M17 10.5a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z"></path>
            </svg>
          </span>
          <input
            type="text"
            [(ngModel)]="filtroBusqueda"
            placeholder="Buscar por nombre, clave o categoría..."
            class="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-800 placeholder-gray-400 focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500" />
        </div>
      </div>
    </div>
  </div>

  <div class="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
    @if (loading) {
      <div class="flex items-center justify-center px-6 py-16">
        <div class="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-500"></div>
        <span class="ml-3 text-sm text-gray-500 dark:text-gray-400">Cargando plantillas...</span>
      </div>
    }

    @if (error) {
      <div class="m-6 rounded-xl border border-error-200 bg-error-50 p-4 dark:border-error-500/20 dark:bg-error-500/10">
        <p class="text-sm text-error-600 dark:text-error-400">{{ error }}</p>
      </div>
    }

    @if (!loading && !error) {
      <div class="overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead>
            <tr class="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:border-gray-800 dark:text-gray-400">
              <th class="px-4 py-3">Plantilla</th>
              <th class="px-4 py-3">Categoría</th>
              <th class="px-4 py-3">Tipo por defecto</th>
              <th class="px-4 py-3">Origen</th>
              <th class="px-4 py-3">Creada</th>
              <th class="px-4 py-3 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-800">
            @for (template of templatesFiltradas; track template.id) {
              <tr class="align-top transition hover:bg-gray-50 dark:hover:bg-gray-800/40">
                <td class="px-4 py-4">
                  <div class="font-medium text-gray-800 dark:text-white">{{ template.name }}</div>
                  <div class="mt-1 text-xs text-gray-500 dark:text-gray-400">{{ template.templateKey }}</div>
                  @if (template.description) {
                    <p class="mt-2 max-w-md text-sm text-gray-500 dark:text-gray-400">{{ template.description }}</p>
                  }
                </td>

                <td class="px-4 py-4 text-gray-600 dark:text-gray-300">
                  {{ getCategoryLabel(template.category) || '—' }}
                </td>

                <td class="px-4 py-4 text-gray-600 dark:text-gray-300">
                  {{ getTypeLabel(template.defaultChatflowType) }}
                </td>

                <td class="px-4 py-4">
                  @if (esGlobal(template)) {
                    <app-badge color="info">Sistema</app-badge>
                  } @else {
                    <app-badge color="primary">Propia</app-badge>
                  }
                </td>

                <td class="px-4 py-4 text-gray-500 dark:text-gray-400">
                  {{ template.datetimeCreated ? (template.datetimeCreated | date:'dd/MM/yyyy HH:mm') : '—' }}
                </td>

                <td class="px-4 py-4">
                  <div class="flex items-center justify-center gap-1">
                    <!-- Ver detalle (ambas) -->
                    <button (click)="verDetalle(template)" title="Ver detalle"
                      class="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200">
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                      </svg>
                    </button>

                    <!-- Editar (solo propias) -->
                    @if (!esGlobal(template)) {
                      <button (click)="abrirEditar(template)" title="Editar plantilla"
                        class="rounded-lg p-2 text-brand-500 transition-colors hover:bg-brand-50 hover:text-brand-600 dark:text-brand-400 dark:hover:bg-brand-500/10">
                        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                        </svg>
                      </button>
                    } @else {
                      <span class="cursor-not-allowed rounded-lg p-2 text-gray-300 dark:text-gray-600"
                        title="Las plantillas del sistema no se pueden editar (usa Duplicar para crear una propia)">
                        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                        </svg>
                      </span>
                    }

                    <!-- Duplicar (ambas) -->
                    <button (click)="duplicarTemplate(template)" [disabled]="duplicatingId === template.id"
                      [title]="duplicatingId === template.id ? 'Duplicando...' : 'Duplicar plantilla'"
                      class="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-wait disabled:opacity-60 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200">
                      @if (duplicatingId === template.id) {
                        <span class="block h-4 w-4 animate-spin rounded-full border-b-2 border-gray-400"></span>
                      } @else {
                        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                        </svg>
                      }
                    </button>

                    <!-- Eliminar (solo propias) -->
                    @if (!esGlobal(template)) {
                      <button (click)="eliminarTemplate(template)" [disabled]="deletingId === template.id"
                        [title]="deletingId === template.id ? 'Eliminando...' : 'Eliminar plantilla'"
                        class="rounded-lg p-2 text-error-500 transition-colors hover:bg-error-50 hover:text-error-600 disabled:cursor-wait disabled:opacity-60 dark:text-error-400 dark:hover:bg-error-500/10 dark:hover:text-error-300">
                        @if (deletingId === template.id) {
                          <span class="block h-4 w-4 animate-spin rounded-full border-b-2 border-error-500"></span>
                        } @else {
                          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                          </svg>
                        }
                      </button>
                    } @else {
                      <span class="cursor-not-allowed rounded-lg p-2 text-gray-300 dark:text-gray-600"
                        title="Las plantillas globales del sistema no se pueden eliminar">
                        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                        </svg>
                      </span>
                    }
                  </div>
                </td>
              </tr>
            }
            @empty {
              <tr>
                <td colspan="6" class="px-4 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                  {{ filtroBusqueda ? 'No hay plantillas que coincidan con la búsqueda.' : 'No hay plantillas en la biblioteca.' }}
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </div>
</div>

<!-- Modal: Ver detalle de plantilla -->
<app-modal [isOpen]="showDetailModal" (close)="cerrarDetalle()" className="max-w-2xl mx-4 p-6">
  <h3 class="text-lg font-semibold text-gray-800 dark:text-white">Detalle de la plantilla</h3>

  @if (detailLoading) {
    <div class="flex items-center gap-3 py-10">
      <span class="block h-6 w-6 animate-spin rounded-full border-b-2 border-brand-500"></span>
      <span class="text-sm text-gray-500 dark:text-gray-400">Cargando detalle…</span>
    </div>
  }

  @if (detailError) {
    <div class="mt-4 rounded-xl border border-error-200 bg-error-50 p-3 text-sm text-error-600 dark:border-error-500/20 dark:bg-error-500/10 dark:text-error-400">
      {{ detailError }}
    </div>
  }

  @if (detailTemplate && !detailLoading) {
    <div class="mt-4 space-y-4">
      <div class="flex flex-wrap items-center gap-2">
        <span class="text-lg font-semibold text-gray-800 dark:text-white">{{ detailTemplate.name }}</span>
        @if (esGlobal(detailTemplate)) {
          <app-badge color="info">Sistema</app-badge>
        } @else {
          <app-badge color="primary">Propia</app-badge>
        }
      </div>

      <dl class="grid gap-4 sm:grid-cols-2 text-sm">
        <div>
          <dt class="text-gray-500 dark:text-gray-400">Clave técnica</dt>
          <dd class="mt-0.5 font-mono text-gray-800 dark:text-gray-200">{{ detailTemplate.templateKey }}</dd>
        </div>
        <div>
          <dt class="text-gray-500 dark:text-gray-400">Categoría</dt>
          <dd class="mt-0.5 text-gray-800 dark:text-gray-200">{{ getCategoryLabel(detailTemplate.category) || '—' }}</dd>
        </div>
        <div>
          <dt class="text-gray-500 dark:text-gray-400">Tipo por defecto</dt>
          <dd class="mt-0.5 text-gray-800 dark:text-gray-200">{{ getTypeLabel(detailTemplate.defaultChatflowType) }}</dd>
        </div>
        <div>
          <dt class="text-gray-500 dark:text-gray-400">Creada</dt>
          <dd class="mt-0.5 text-gray-800 dark:text-gray-200">{{ detailTemplate.datetimeCreated ? (detailTemplate.datetimeCreated | date:'dd/MM/yyyy HH:mm') : '—' }}</dd>
        </div>
      </dl>

      @if (detailTemplate.description) {
        <div>
          <div class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Descripción</div>
          <p class="mt-1 text-sm text-gray-600 dark:text-gray-300">{{ detailTemplate.description }}</p>
        </div>
      }

      @if (detailDefinitionPreview) {
        <div>
          <div class="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Vista previa de la definición base</div>
          <pre class="max-h-64 overflow-auto rounded-xl bg-gray-950 p-4 text-xs text-gray-200 whitespace-pre-wrap break-all">{{ detailDefinitionPreview }}</pre>
        </div>
      }
    </div>

    <div class="mt-6 flex flex-wrap justify-end gap-3">
      <button (click)="duplicarTemplate(detailTemplate)" [disabled]="duplicatingId === detailTemplate.id"
        class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
        Duplicar
      </button>
      @if (!esGlobal(detailTemplate)) {
        <button (click)="abrirEditar(detailTemplate); cerrarDetalle()"
          class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600">
          Editar
        </button>
      }
      <button (click)="cerrarDetalle()"
        class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
        Cerrar
      </button>
    </div>
  }
</app-modal>

<!-- Modal: Editar plantilla (solo propias) -->
<app-modal [isOpen]="showEditModal" (close)="cerrarEditar()" className="max-w-md mx-4 p-6">
  <h3 class="text-lg font-semibold text-gray-800 dark:text-white">Editar plantilla</h3>
  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
    Solo puedes editar plantillas propias de tu negocio.
  </p>

  @if (editError) {
    <div class="mt-4 rounded-xl border border-error-200 bg-error-50 p-3 text-sm text-error-600 dark:border-error-500/20 dark:bg-error-500/10 dark:text-error-400">
      {{ editError }}
    </div>
  }

  <div class="mt-5 space-y-4">
    <div>
      <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
        Nombre <span class="text-error-500">*</span>
      </label>
      <input type="text" [(ngModel)]="editNombre" maxlength="120" placeholder="Nombre de la plantilla"
        class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500" />
    </div>

    <div>
      <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Descripción</label>
      <textarea [(ngModel)]="editDescripcion" rows="3" maxlength="500" placeholder="¿Para qué sirve esta plantilla? (opcional)"
        class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"></textarea>
    </div>

    <div class="grid gap-4 sm:grid-cols-2">
      <div>
        <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Categoría</label>
        <select [(ngModel)]="editCategoria"
          class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white">
          <option value="">Sin categoría</option>
          @for (categoria of categoriasPlantilla; track categoria.value) {
            <option [value]="categoria.value">{{ categoria.label }}</option>
          }
        </select>
      </div>
      <div>
        <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Orden</label>
        <input type="number" [(ngModel)]="editSortOrder" placeholder="Ej. 10"
          class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
      </div>
    </div>

    <div>
      <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">URL de miniatura</label>
      <input type="url" [(ngModel)]="editThumbnail" placeholder="https://tu-dominio.com/preview.png"
        class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500" />
    </div>

    <label class="flex items-center gap-2 cursor-pointer">
      <input type="checkbox" [(ngModel)]="editActive"
        class="rounded border-gray-300 text-brand-500 focus:ring-brand-500" />
      <span class="text-sm text-gray-700 dark:text-gray-300">Plantilla activa (visible al crear chatflows)</span>
    </label>
  </div>

  <div class="mt-6 flex justify-end gap-3">
    <button (click)="cerrarEditar()" [disabled]="savingEdit"
      class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
      Cancelar
    </button>
    <button (click)="guardarEdicion()" [disabled]="savingEdit || !editNombre.trim()"
      class="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60">
      @if (savingEdit) {
        <span class="block h-4 w-4 animate-spin rounded-full border-b-2 border-white"></span>
      }
      Guardar cambios
    </button>
  </div>
</app-modal>

<!-- Inc 5: "Crear plantilla" crea el registro en la biblioteca de plantillas, no un chatflow -->
<app-modal [isOpen]="showCreateModal" (close)="cerrarCrear()" className="max-w-md mx-4 p-6">
  <h3 class="text-lg font-semibold text-gray-800 dark:text-white">Crear plantilla</h3>
  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
    La plantilla se guarda en esta biblioteca y podrás usarla al crear nuevos chatflows.
  </p>

  @if (createError) {
    <div class="mt-4 rounded-xl border border-error-200 bg-error-50 p-3 text-sm text-error-600 dark:border-error-500/20 dark:bg-error-500/10 dark:text-error-400">
      {{ createError }}
    </div>
  }

  <div class="mt-5 space-y-4">
    <div>
      <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
        Nombre <span class="text-error-500">*</span>
      </label>
      <input type="text" [(ngModel)]="createNombre" maxlength="120" placeholder="Nombre de la plantilla"
        class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500" />
    </div>

    <div>
      <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Descripción</label>
      <textarea [(ngModel)]="createDescripcion" rows="3" maxlength="500" placeholder="¿Para qué sirve esta plantilla? (opcional)"
        class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"></textarea>
    </div>

    <div class="grid gap-4 sm:grid-cols-2">
      <div>
        <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Categoría</label>
        <select [(ngModel)]="createCategoria"
          class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white">
          @for (categoria of categoriasPlantilla; track categoria.value) {
            <option [value]="categoria.value">{{ categoria.label }}</option>
          }
        </select>
      </div>
      <div>
        <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Tipo sugerido</label>
        <select [(ngModel)]="createTipo"
          class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white">
          <option value="BOT">{{ getTypeLabel('BOT') }}</option>
          <option value="LIVE_CHAT">{{ getTypeLabel('LIVE_CHAT') }}</option>
          <option value="AI_CHATBOT">{{ getTypeLabel('AI_CHATBOT') }}</option>
        </select>
      </div>
    </div>

    <div>
      <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Flujo inicial</label>
      <select [(ngModel)]="createOrigenChatflowId"
        class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white">
        <option value="">Flujo básico (inicio → bienvenida → fin)</option>
        @for (chatflow of chatflowsOrigen; track chatflow.id) {
          <option [value]="chatflow.id">Copiar flujo de: {{ chatflow.name }}</option>
        }
      </select>
    </div>
  </div>

  <div class="mt-6 flex justify-end gap-3">
    <button (click)="cerrarCrear()" [disabled]="savingCreate"
      class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
      Cancelar
    </button>
    <button (click)="guardarCreacion()" [disabled]="savingCreate || !createNombre.trim()"
      class="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60">
      @if (savingCreate) {
        <span class="block h-4 w-4 animate-spin rounded-full border-b-2 border-white"></span>
      }
      Crear plantilla
    </button>
  </div>
</app-modal>
